import { rules } from './config.js'
import {
    REPLAY_MOVE_MS,
    REPLAY_BUILD_MS,
    REPLAY_DEPLOY_GLIDE_MS,
    REPLAY_DEPLOY_STAGGER_MS,
    REPLAY_SETTLE_MS,
} from './gameConfig.js'

// ---------------------------------------------------------------------------
// Corridor slots (positional unit model)
// ---------------------------------------------------------------------------
//
// Each corridor carries a positional `slots` array alongside its unit counts:
// slots[plot] = null | token, plot 0 = the front (nearest the center line),
// length = maxArmyQty. Tokens are { uid, kind } with kind one of
// 'vet' | 'moved' | 'green' (greens may carry delivered: true).
//
// The Base is NOT a slot token: it is a ground structure on the Grid
// (subplotBases.js). The corridor keeps the combat bookkeeping — fortCount /
// fortInConstruction / fortProgress / baseCenterS — but the Base occupies no
// plot and consumes no corridor capacity: capacity governs airborne drones
// only, and drones stand in the corridor above a Base freely.
//
// Rules:
//   • Drones pack front-first: when a plot opens, drones behind it slide
//     forward to close the gap.
//   • New arrivals (deploys, moved-in drones, drop-zone deliveries) enter at
//     the first open plot — always behind every drone already there.
//
// Counts stay the combat source of truth; slots only decide where each unit
// stands. ensureSlots rebuilds them when a legacy board (saved before this
// model existed, including boards with the old anchored base tokens) or a
// drifted array disagrees with the counts.

function isBaseKind(kind) { return kind === 'base' || kind === 'baseCon' }

// Missiles (in front→back order) re-packed front-first.
function packWithMissiles(slots, capacity, missiles) {
    const out = Array(capacity).fill(null)
    let p = 0
    for (const m of missiles) {
        while (p < capacity && out[p] !== null) p++
        if (p < capacity) out[p] = m
    }
    return out
}

function missilesOf(slots) {
    return slots.filter(t => t && !isBaseKind(t.kind))
}

function packSlots(slots, capacity) {
    return packWithMissiles(slots, capacity, missilesOf(slots))
}

// True when bf.slots agrees with the unit counts. Any legacy base token in the
// slots is a mismatch — the rebuild strips it (Bases live on the Grid now).
function slotsMatchCounts(bf) {
    const slots = bf.slots
    if (!Array.isArray(slots) || slots.length !== bf.maxArmyQty) return false
    const c = { vet: 0, moved: 0, green: 0, delivered: 0 }
    for (const t of slots) {
        if (!t) continue
        if (c[t.kind] === undefined) return false
        c[t.kind]++
        if (t.kind === 'green' && t.delivered) c.delivered++
    }
    return c.vet     === (bf.armyQty            || 0)
        && c.moved   === (bf.movedInArmies      || 0)
        && c.green   === (bf.newArmies          || 0)
        && c.delivered === Math.min(bf.deliveredNewArmies || 0, bf.newArmies || 0)
}

// Returns a bf whose slots agree with its counts. A matching board passes
// through untouched; a missing or drifted array is rebuilt in the legacy
// display order (veterans, moved-in, then greens with delivered greens at
// the back).
export function ensureSlots(bf) {
    if (slotsMatchCounts(bf)) return bf
    let uid = 1
    const tokens = []
    const push = (kind, extra) => tokens.push({ uid: uid++, kind, ...extra })
    for (let i = 0; i < (bf.armyQty || 0); i++)            push('vet')
    for (let i = 0; i < (bf.movedInArmies || 0); i++)      push('moved')
    const greens    = bf.newArmies || 0
    const delivered = Math.min(bf.deliveredNewArmies || 0, greens)
    for (let i = 0; i < greens; i++) push('green', i >= greens - delivered ? { delivered: true } : {})
    const slots = Array(bf.maxArmyQty).fill(null)
    for (let i = 0; i < Math.min(tokens.length, bf.maxArmyQty); i++) slots[i] = tokens[i]
    return { ...bf, slots, nextUid: uid }
}

// The first open plot — where the next arrival enters (behind every drone
// already standing).
function appendToken(bf, token) {
    const slots = [...bf.slots]
    const i = slots.indexOf(null)
    if (i !== -1) slots[i] = token
    return slots
}

// A moved-in drone packs in AHEAD of the round's fresh deploys: after the
// last veteran / moved-in token, before the first green — the corridor's
// canonical order (veterans, moved-in, then new arrivals; ensureSlots
// rebuilds in the same order), whatever order the orders were given in.
// The opponent's replay shows the moves before the deploys, so the
// submitted board has to read that way too, or the hop lands on the
// wrong plot (the slot the deploys left it) and the greens then slide
// in ahead of it.
function insertMovedToken(slots, capacity, token) {
    const missiles = missilesOf(slots)
    const firstGreen = missiles.findIndex(t => t.kind === 'green')
    const at = firstGreen === -1 ? missiles.length : firstGreen
    missiles.splice(at, 0, token)
    return packWithMissiles(slots, capacity, missiles)
}

// Remove the token at `index` and slide the missiles behind it forward.
function removeTokenAt(slots, index, capacity) {
    const next = [...slots]
    next[index] = null
    return packSlots(next, capacity)
}

// ---------------------------------------------------------------------------
// Combat strength
// ---------------------------------------------------------------------------

// One mature brood's battle worth: its LIVE DRONES — the two on its outer
// cells (subplotBases.broodStrength; hp is
// maintained by computeAttacks' post-crater sync). At zero the brood is dead
// (subplotGrid.isStructureDestroyed) and leaves the ledger at the next sync.
export function baseStrength(base) {
    if (!base?.mature) return 0
    const { fortStrength } = rules()
    return Math.max(0, Math.min(fortStrength, base.hp ?? fortStrength))
}

// Total mature-Base strength on a corridor. Legacy boards carry a bare
// fortCount with no per-base ledger — those count full value.
export function fortStrengthOf(bf) {
    const mature = basesOf(bf).filter(b => b.mature)
    return mature.length
        ? mature.reduce((sum, b) => sum + baseStrength(b), 0)
        : (bf?.fortCount || 0) * rules().fortStrength
}

// A corridor's full strength: its drones plus its mature broods' drones —
// what it fires when it attacks (everything shoots, flyers and broods alike).
export function calculateCombatStrength(battlefield) {
    const armyStrength            = battlefield.armyQty               || 0
    const fortInConstructionStrength = (battlefield.fortInConstruction || 0) * rules().fortInConstructionStrength
    return armyStrength + fortInConstructionStrength + fortStrengthOf(battlefield)
}

// A DEFENDING corridor's broods hold their fire when its flying drones alone
// already match or beat the attack: then the flyers fight alone and the
// broods are not spent. Short of the attack, the broods fire too (and are
// spent, as any brood that fires). Returns { strength, broodsFire }.
export function defenseStrength(bf, attackStrength) {
    const flyers = totalUnits(bf)
    if (flyers >= attackStrength) return { strength: flyers, broodsFire: false }
    return { strength: calculateCombatStrength(bf), broodsFire: true }
}

// ---------------------------------------------------------------------------
// Battle resolution
// ---------------------------------------------------------------------------

// Resolves combat on a single corridor from self's perspective.
// Returns: { type, context, selfStrength, oppStrength, selfBroodsFired, oppBroodsFired }
//   type:    'WIN' | 'LOSS' | 'TIE' | 'NO_BATTLE'
//   context: 'both_attacked' | 'self_attacked' | 'opp_attacked' | null
// An attacker fires everything (calculateCombatStrength); a defender's broods
// fire only if its flyers fall short of the attack (defenseStrength).
// *BroodsFired says whether that side's mature broods fired — and so are
// spent when the corridor clears.
export function resolveBattlefield(selfBf, oppBf) {
    let selfStrength = calculateCombatStrength(selfBf)
    let oppStrength = calculateCombatStrength(oppBf)
    let selfBroodsFired = !!selfBf.isAttacking
    let oppBroodsFired = !!oppBf.isAttacking
    if (!selfBf.isAttacking && oppBf.isAttacking) {
        const d = defenseStrength(selfBf, oppStrength)
        selfStrength = d.strength; selfBroodsFired = d.broodsFire
    } else if (selfBf.isAttacking && !oppBf.isAttacking) {
        const d = defenseStrength(oppBf, selfStrength)
        oppStrength = d.strength; oppBroodsFired = d.broodsFire
    }
    const fired = { selfBroodsFired, oppBroodsFired }

    if (selfBf.isAttacking && oppBf.isAttacking) {
        if (selfStrength > oppStrength) return { type: 'WIN',  context: 'both_attacked', selfStrength, oppStrength, ...fired }
        if (selfStrength < oppStrength) return { type: 'LOSS', context: 'both_attacked', selfStrength, oppStrength, ...fired }
        return                                 { type: 'TIE',  context: 'both_attacked', selfStrength, oppStrength, ...fired }
    }

    if (selfBf.isAttacking && !oppBf.isAttacking) {
        if (selfStrength > oppStrength) return { type: 'WIN',  context: 'self_attacked', selfStrength, oppStrength, ...fired }
        if (selfStrength === oppStrength) return { type: 'TIE', context: 'self_attacked', selfStrength, oppStrength, ...fired }
        return                                  { type: 'LOSS', context: 'self_attacked', selfStrength, oppStrength, ...fired }
    }

    if (!selfBf.isAttacking && oppBf.isAttacking) {
        if (selfStrength > oppStrength) return { type: 'WIN',  context: 'opp_attacked', selfStrength, oppStrength, ...fired }
        if (selfStrength === oppStrength) return { type: 'TIE', context: 'opp_attacked', selfStrength, oppStrength, ...fired }
        return                                  { type: 'LOSS', context: 'opp_attacked', selfStrength, oppStrength, ...fired }
    }

    return { type: 'NO_BATTLE', context: null, selfStrength, oppStrength }
}

// Clears all units on a single corridor after a win/loss.
// The Base works like a rocket: it's spent by FIRING, not by the corridor
// clearing. A MATURE Base that fired in the battle (always when its corridor
// attacked; when defending, only if the flyers fell short of the attack —
// resolveBattlefield's *BroodsFired) dies with the clear; one that held its
// fire stays. A Base UNDER
// CONSTRUCTION is worth 0 and fires nothing — it survives every battle and
// only ever falls to Grid damage. computeAttacks removes the fired Base's
// body on the Hive when it sees fortCount drop.
function clearBattlefield(bf, broodsFired = true) {
    // Mature Bases that fired are spent; ones that held their fire stay.
    // Constructions fire nothing and always survive, keeping their positions
    // and clocks.
    const surviving = broodsFired ? basesOf(bf).filter(b => !b.mature) : basesOf(bf)
    return {
        ...bf,
        armyQty: 0,
        isAttacking: false,
        fortCount: surviving.filter(b => b.mature).length,
        fortInConstruction: surviving.filter(b => !b.mature).length,
        bases: surviving,
        ...(surviving.length ? {} : { pendingBaseBuilds: null }),
        fortMovedInFunded: 0,
        slots: Array(bf.maxArmyQty).fill(null),
    }
}

// Advances every surviving Base-in-construction by one survived round. Called
// once after all battles resolve. Not exported — only called internally by
// resolveAllBattles. Each Base keeps its own clock: it matures once it has
// survived FORT_MATURATION_ROUNDS rounds of attacks; its body on the Hive flips
// to operational in computeAttacks when the ledger says so.
function advanceFortConstruction(boardArr) {
    return boardArr.map(bf0 => {
        if (bf0.fortInConstruction > 0) {
            const bases = basesOf(bf0).map(b => {
                if (b.mature) return b
                const progress = (b.progress || 0) + 1
                return progress >= rules().fortMaturationRounds
                    ? { ...b, progress: 0, mature: true }
                    : { ...b, progress }
            })
            return {
                ...bf0,
                bases,
                fortCount: bases.filter(b => b.mature).length,
                fortInConstruction: bases.filter(b => !b.mature).length,
                // Construction that survived a battle keeps its investment —
                // the undo records die here (submit clears them too; this is
                // the backstop).
                pendingBaseBuilds: null,
            }
        }
        return bf0
    })
}

// Resolves all 7 battles IN CORRIDOR ORDER (1 → 7) and advances forts-in-
// construction one survived round. Always returns one event per corridor
// (7 total), including NO_BATTLE for inactive ones.
//
// Order matters. Each corridor litigates against the boards AS THEY STAND
// when its turn comes, and the optional `afterCorridor` hook runs between
// corridors — the war flow uses it to land the corridor's crater and feed
// the Grid damage straight back into the ledgers, so a Base that corridor
// 1's blast cratered fights corridor 2 at its reduced live-cell strength
// (or not at all, if it died). The hook receives
// (index, event, selfBoard, oppBoard, { selfBf, oppBf }) — the last pair
// being the two corridors exactly as they ENTERED the clash, before the
// clear — and may return { selfBoard, oppBoard } to replace the running
// boards for every corridor still to come.
// selfPointsGained / oppPointsGained are relative to the caller — use
// isSelfPlayer1 to map back to absolute player1/player2 scores.
export function resolveAllBattles(selfBoard, oppBoard, { afterCorridor = null } = {}) {
    let selfBoardResult = selfBoard.map(bf => ({ ...bf }))
    let oppBoardResult  = oppBoard.map(bf => ({ ...bf }))
    let selfPointsGained = 0
    let oppPointsGained  = 0
    const events = []

    for (let index = 0; index < selfBoard.length; index++) {
        const selfBf = selfBoardResult[index]
        const oppBf  = oppBoardResult[index]
        const result = resolveBattlefield(selfBf, oppBf)
        const event = { ...result, battlefieldId: selfBf.id }
        events.push(event)

        if (result.type === 'WIN')       selfPointsGained++
        else if (result.type === 'LOSS') oppPointsGained++
        if (result.type !== 'NO_BATTLE') {
            selfBoardResult[index] = clearBattlefield(selfBf, result.selfBroodsFired)
            oppBoardResult[index]  = clearBattlefield(oppBf, result.oppBroodsFired)
        }

        if (afterCorridor) {
            const next = afterCorridor(index, event, selfBoardResult, oppBoardResult, { selfBf, oppBf })
            if (next?.selfBoard) selfBoardResult = next.selfBoard
            if (next?.oppBoard)  oppBoardResult  = next.oppBoard
        }
    }

    // Surviving fort construction advances (and matures) after all battles resolve
    selfBoardResult = advanceFortConstruction(selfBoardResult)
    oppBoardResult  = advanceFortConstruction(oppBoardResult)

    return { selfBoard: selfBoardResult, oppBoard: oppBoardResult, selfPointsGained, oppPointsGained, events }
}

// ---------------------------------------------------------------------------
// Homefront damage & reveal helpers
// ---------------------------------------------------------------------------

// absoluteIndex layout: col * 5 + row, col 0–6, row 0–4 (row 0 = closest to corridor)

// Count structures in all 8 surrounding plots (diagonal + cardinal).
// v2: all 35 plots are always in play — warCapacity no longer gates targeting,
// so every neighbor counts.
export function computeMinesweeperCount(plots, absoluteIndex) {
    const col = Math.floor(absoluteIndex / 5)
    const row = absoluteIndex % 5
    let count = 0
    for (let dc = -1; dc <= 1; dc++) {
        for (let dr = -1; dr <= 1; dr++) {
            if (dc === 0 && dr === 0) continue
            const nc = col + dc
            const nr = row + dr
            if (nc < 0 || nc > 6 || nr < 0 || nr > 4) continue
            const neighbor = plots[nc * 5 + nr]
            if (neighbor?.hasStructure || neighbor?.structureDestroyed) count++
        }
    }
    return count
}

// ---------------------------------------------------------------------------
// Attack validation
// ---------------------------------------------------------------------------

// Validates turning attack ON only. Do not call when isAttacking is already
// true — toggle-off requires no validation.
// Attack rights belong to corridors that had veterans at round start: local
// veterans still standing, movedOut proving one stood here before moving
// away this round, or a brood built this round (its funding pair were
// veterans that started the round here). A mature brood carries its own
// attack rights. A brood whose drones are both dead is dead
// (subplotGrid.isStructureDestroyed) and leaves the ledger when the round
// that killed it resolves, so it grants nothing after that. The ledger's
// stored hp is not consulted here: wars saved before an earlier fix can hold
// a live brood at hp 0, and the Grid, read at resolution, is the truth.
// On top of the rights, something must be standing to fight with —
// veterans, moved-in veterans (they merge into armyQty at submit, before
// battles resolve), or a mature brood.
// A Base UNDER CONSTRUCTION does not block the corridor: it sits on the Grid
// below the airspace (fires nothing, counts 0), so the drones above it fight
// freely — the old corridor-slot-era restriction is gone.
export function canAttack(battlefield) {
    if (battlefield.attackHold > 0) {
        const when = battlefield.attackHold === 1 ? 'next round' : `in ${battlefield.attackHold} rounds`
        return { valid: false, reason: `Cannot attack yet: attacks open ${when}.` }
    }
    if (battlefield.newArmies > 0) {
        return { valid: false, reason: 'Cannot attack from Corridor with newly deployed armies.' }
    }
    const broods = battlefield.fortCount || 0
    const broodedHere = (battlefield.pendingBaseBuilds ?? []).length > 0
    const hadRights = (battlefield.armyQty || 0) > 0 || broods > 0 || !!battlefield.movedOut || broodedHere
    const canFight  = (battlefield.armyQty || 0) > 0 || broods > 0 || (battlefield.movedInArmies || 0) > 0
    if (!hadRights || !canFight) {
        return { valid: false, reason: 'Cannot attack from Corridor with no drones/Bases at round start.' }
    }
    return { valid: true, reason: null }
}

// ---------------------------------------------------------------------------
// Movement
// ---------------------------------------------------------------------------

// Computes the adjacent corridor index. No wrap-around: stepping off either
// end of the board returns null (edge corridors can only move inward).
function adjacentIndex(fromIndex, direction, boardLength) {
    if (direction === 'right') {
        return fromIndex + 1 > boardLength - 1 ? null : fromIndex + 1
    }
    if (direction === 'left') {
        return fromIndex - 1 < 0 ? null : fromIndex - 1
    }
    return null
}

// Total units on a corridor for capacity checking purposes. Corridor capacity
// governs AIRBORNE drones only — Bases sit on the Grid below the airspace
// and consume no capacity (drones occupy the corridor above them freely).
function totalUnits(bf) {
    return (bf.armyQty || 0) + (bf.newArmies || 0) + (bf.movedInArmies || 0)
}

// The corridor's per-Base ledger: [{ centerS, progress, mature }] — a corridor
// holds ANY number of Bases (only Grid real estate limits them), so each keeps
// its own position and maturation clock. fortCount / fortInConstruction stay
// the aggregate combat counters, maintained in lockstep. Reads go through
// basesOf so boards saved under the old one-base scalar model (baseCenterS /
// fortProgress) still resolve.
export function basesOf(bf) {
    if (Array.isArray(bf?.bases)) return bf.bases
    if (bf?.baseCenterS != null) {
        if ((bf.fortCount || 0) > 0) return [{ centerS: bf.baseCenterS, progress: 0, mature: true }]
        if ((bf.fortInConstruction || 0) > 0) return [{ centerS: bf.baseCenterS, progress: bf.fortProgress || 0, mature: false }]
    }
    return []
}

// Moves one army from the corridor with the given id, in the given direction
// ('left' | 'right' — the player picks per move; there is no standing plan).
// Forts are immobile and are never moved. The FRONT-MOST veteran is the one
// that moves (the missile carrying the move-ready stroke); the missiles behind
// it slide forward to close the gap.
// Returns { success, reason, boardArr }
// `uid` names the veteran that leaves (the one the player dragged); omitted,
// the front-most goes. Either way the rest pack forward — veterans are
// interchangeable, so the board that results is the same; the uid only
// keeps the tile the player touched the one that travels.
export function moveUnit(boardArr, fromId, direction, uid = null) {
    const fromIndex = boardArr.findIndex(bf => bf.id === fromId)
    if (fromIndex === -1) return { success: false, reason: `Corridor ${fromId} not found.`, boardArr }

    const bf = ensureSlots(boardArr[fromIndex])

    if (bf.isAttacking) {
        return { success: false, reason: 'Cannot move attacking armies.', boardArr }
    }

    if (!bf.armyQty) {
        return { success: false, reason: `No previously deployed armies on Corridor ${fromId}.`, boardArr }
    }

    if (bf.movedOut) {
        return { success: false, reason: `Movement denied. Already moved from Corridor ${fromId}.`, boardArr }
    }

    const toIndex = adjacentIndex(fromIndex, direction, boardArr.length)
    if (toIndex === null) {
        return { success: false, reason: `No Corridor in that direction from Corridor ${fromId}.`, boardArr }
    }

    const dest = ensureSlots(boardArr[toIndex])
    if (totalUnits(dest) >= dest.maxArmyQty) {
        return { success: false, reason: 'Movement denied. Receiving Corridor full.', boardArr }
    }

    const newBoard = boardArr.map(b => ({ ...b }))

    // Destination: the moved missile packs in behind the veterans and
    // moved-in drones already there, ahead of any fresh deploys.
    // Each moved-in drone carries the SIDE it came from (`from`): a corridor
    // can take one from each neighbour in a round, so the corridor-level
    // movedFrom (the latest arrival's, kept for old saves) can't tell two
    // arrivals apart.
    const from = direction === 'right' ? 'left' : 'right'
    newBoard[toIndex] = {
        ...dest,
        movedFrom: from,
        movedInArmies: (dest.movedInArmies || 0) + 1,
        slots: insertMovedToken(dest.slots, dest.maxArmyQty, { uid: dest.nextUid || 1, kind: 'moved', from }),
        nextUid: (dest.nextUid || 1) + 1,
    }

    // Source: the named veteran (else the front-most) leaves; everyone
    // behind slides forward.
    const named = uid != null ? bf.slots.findIndex(t => t && t.kind === 'vet' && t.uid === uid) : -1
    const vi = named !== -1 ? named : bf.slots.findIndex(t => t && t.kind === 'vet')
    newBoard[fromIndex] = {
        ...bf,
        movedOut: true,
        armyQty: bf.armyQty - 1,
        slots: vi === -1 ? bf.slots : removeTokenAt(bf.slots, vi, bf.maxArmyQty),
    }

    return { success: true, reason: null, boardArr: newBoard }
}

// The side a moved-in drone came from: its own record, or — on a board saved
// before drones carried one — the corridor's.
export function movedFromOf(bf, token) {
    return token?.from ?? bf?.movedFrom ?? null
}

// Undoes a move into corridor toId: the moved-in drone `uid` (the one the
// player picked up or tapped), or without one the most recent arrival. The
// drone goes back to the side IT came from, and that corridor's move is
// unspent again (a corridor moves at most one drone a round).
// Returns { success, reason, boardArr }
export function undoMove(boardArr, toId, uid = null) {
    const toIndex = boardArr.findIndex(bf => bf.id === toId)
    if (toIndex === -1) return { success: false, reason: `Corridor ${toId} not found.`, boardArr }

    const bf = boardArr[toIndex]
    if (!bf.movedInArmies) return { success: false, reason: 'No moved unit to undo.', boardArr }
    const destBf = ensureSlots(bf)

    let mi = uid != null ? destBf.slots.findIndex(t => t?.kind === 'moved' && t.uid === uid) : -1
    if (mi === -1) {
        for (let i = destBf.slots.length - 1; i >= 0; i--) {
            if (destBf.slots[i]?.kind === 'moved') { mi = i; break }
        }
    }
    const side = movedFromOf(bf, mi === -1 ? null : destBf.slots[mi])
    if (!side) return { success: false, reason: 'No moved unit to undo.', boardArr }

    // No wrap-around: a consistent side always points at an in-bounds
    // neighbour, so an out-of-range index means corrupted state.
    const fromIndex = side === 'right' ? toIndex + 1 : toIndex - 1
    if (fromIndex < 0 || fromIndex > boardArr.length - 1) {
        return { success: false, reason: 'No moved unit to undo.', boardArr }
    }

    // Refuse undo if restoring the unit would overflow the source corridor.
    // This happens when a new army was deployed into the vacated slot after the move.
    const sourceBf = boardArr[fromIndex]
    if (totalUnits(sourceBf) >= sourceBf.maxArmyQty) {
        return { success: false, reason: 'Cannot undo move. The source corridor is now full.', boardArr }
    }

    const srcBf  = ensureSlots(sourceBf)
    const newBoard = boardArr.map(b => ({ ...b }))

    // Restore source corridor — the missile returns to the front (it left as
    // the front-most veteran), sliding the others back; its move is unspent.
    newBoard[fromIndex] = {
        ...srcBf,
        movedOut: false,
        armyQty: srcBf.armyQty + 1,
        slots: packWithMissiles(srcBf.slots, srcBf.maxArmyQty,
            [{ uid: srcBf.nextUid || 1, kind: 'vet' }, ...missilesOf(srcBf.slots)]),
        nextUid: (srcBf.nextUid || 1) + 1,
    }

    // Update destination — remove that moved-in drone; the corridor's
    // movedFrom follows the arrival that is left (if any).
    const slots = mi === -1 ? destBf.slots : removeTokenAt(destBf.slots, mi, destBf.maxArmyQty)
    const left = slots.filter(t => t?.kind === 'moved')
    const otherSide = side === 'right' ? 'left' : 'right'
    newBoard[toIndex] = {
        ...destBf,
        movedInArmies: destBf.movedInArmies - 1,
        movedFrom: destBf.movedInArmies === 1 ? null : (left[left.length - 1]?.from ?? otherSide),
        slots,
    }

    return { success: true, reason: null, boardArr: newBoard }
}

// ---------------------------------------------------------------------------
// Deployment
// ---------------------------------------------------------------------------

// Deploys one army to corridor id. The missile enters at the first open plot
// (always behind every missile already there).
// Returns { success, reason, boardArr, armiesToDeploy }
// IMPORTANT: returns updated armiesToDeploy — caller must update both board
//            state and armiesToDeploy state separately.
export function deployArmy(boardArr, id, armiesToDeploy) {
    if (armiesToDeploy <= 0) {
        return { success: false, reason: `Only ${armiesToDeploy} armies can be deployed each round.`, boardArr, armiesToDeploy }
    }

    const index = boardArr.findIndex(bf => bf.id === id)
    if (index === -1) return { success: false, reason: `Corridor ${id} not found.`, boardArr, armiesToDeploy }

    const bf = ensureSlots(boardArr[index])

    if (bf.isAttacking) {
        return { success: false, reason: 'Cannot deploy onto Corridor set to launch attack.', boardArr, armiesToDeploy }
    }

    if (totalUnits(bf) >= bf.maxArmyQty) {
        return { success: false, reason: 'Corridor already full.', boardArr, armiesToDeploy }
    }

    const newBoard = boardArr.map(b => ({ ...b }))
    newBoard[index] = {
        ...bf,
        newArmies: bf.newArmies + 1,
        slots: appendToken(bf, { uid: bf.nextUid || 1, kind: 'green' }),
        nextUid: (bf.nextUid || 1) + 1,
    }

    return { success: true, reason: null, boardArr: newBoard, armiesToDeploy: armiesToDeploy - 1 }
}

// Removes one newly deployed army from corridor id.
// Returns { success, reason, boardArr, armiesToDeploy }
export function undoDeploy(boardArr, id, armiesToDeploy) {
    const index = boardArr.findIndex(bf => bf.id === id)
    if (index === -1) return { success: false, reason: `Corridor ${id} not found.`, boardArr, armiesToDeploy }

    const bf = ensureSlots(boardArr[index])

    if (bf.newArmies <= 0) {
        return { success: false, reason: 'No new armies found to remove.', boardArr, armiesToDeploy }
    }

    // Resupply-delivered missiles are bound to their landing column — removing
    // one would recycle it into a deploy-anywhere missile.
    if (bf.newArmies - (bf.deliveredNewArmies || 0) <= 0) {
        return { success: false, reason: 'Resupplied drones cannot be removed.', boardArr, armiesToDeploy }
    }

    // Remove the most recent removable (non-delivered) green missile.
    let gi = -1
    for (let i = bf.slots.length - 1; i >= 0; i--) {
        const t = bf.slots[i]
        if (t && t.kind === 'green' && !t.delivered) { gi = i; break }
    }

    const newBoard = boardArr.map(b => ({ ...b }))
    newBoard[index] = {
        ...bf,
        newArmies: bf.newArmies - 1,
        slots: gi === -1 ? bf.slots : removeTokenAt(bf.slots, gi, bf.maxArmyQty),
    }

    return { success: true, reason: null, boardArr: newBoard, armiesToDeploy: armiesToDeploy + 1 }
}

// ---------------------------------------------------------------------------
// Base (fort) construction
// ---------------------------------------------------------------------------

// Builds one Base for corridor id (costs 2 veteran drones) at Grid position
// `centerS` — the center of its 3-cell bar footprint, pinned to the
// corridor's middle subplot column (the caller validates the Grid side via
// subplotBases.canPlaceBase; this function owns the corridor side).
// The 2 BACK-MOST veterans on the corridor (armyQty or moved-in alike) are
// consumed; their plots open up and the drones in front slide back to pack.
// fortMovedInFunded counts how many consumed drones were moved-in, so
// undoBuildFort can restore each to its source pool: restoring a moved-in
// drone to armyQty would hand it a second move this round. The consumed
// tokens ride on pendingBaseBuilds (funded) so undo can restore their exact
// identity and standing order.
// NO per-corridor cap: any number of Bases, built in one round or across
// rounds — Grid real estate (canPlaceBase + pending-footprint overlap,
// enforced by the caller) is the only limit. 4 veterans = 2 builds, valid.
// Returns { success, reason, boardArr }
export function buildFort(boardArr, id, centerS = null, uids = null) {
    const index = boardArr.findIndex(bf => bf.id === id)
    if (index === -1) return { success: false, reason: `Corridor ${id} not found.`, boardArr }

    const bf = ensureSlots(boardArr[index])

    // A brood is two veterans that STARTED THE ROUND in this corridor (kind
    // 'vet'): a drone moved in this round can't fund one — no moving and
    // then brooding with the mover. (A veteran that moved OUT can't either:
    // it isn't here.)
    const cost = rules().broodCost
    if ((bf.armyQty || 0) < cost) {
        return { success: false, reason: `A brood needs ${cost} veteran drones that started the round in this corridor.`, boardArr }
    }
    if (centerS == null) {
        return { success: false, reason: 'Pick a position for the brood on your Grid.', boardArr }
    }
    if (basesOf(bf).some(b => b.centerS === centerS)) {
        return { success: false, reason: 'A brood already stands there.', boardArr }
    }

    // The funding pair: the two veterans the player put together (uids —
    // the one dragged and the one it was dropped on, or the tapped one and
    // its neighbour), when both stand here as veterans; otherwise the 2
    // back-most veteran tokens, scanning rear→front.
    // (With a broodCost above 2 the named pair is topped up with the next
    // back-most veterans.)
    const isVet = (t) => t && t.kind === 'vet'
    let vetIdxs = []
    if (Array.isArray(uids) && uids.length === 2 && uids[0] !== uids[1]) {
        vetIdxs = uids.map(u => bf.slots.findIndex(t => isVet(t) && t.uid === u))
        if (vetIdxs.some(i => i === -1)) vetIdxs = []
    }
    if (vetIdxs.length > cost) vetIdxs = []
    for (let i = bf.slots.length - 1; i >= 0 && vetIdxs.length < cost; i--) {
        if (isVet(bf.slots[i]) && !vetIdxs.includes(i)) vetIdxs.push(i)
    }
    const consumed    = vetIdxs.map(i => bf.slots[i])
    const fromMovedIn = consumed.filter(t => t.kind === 'moved').length
    const fromArmyQty = cost - fromMovedIn

    let slots = [...bf.slots]
    for (const i of vetIdxs) slots[i] = null
    slots = packSlots(slots, bf.maxArmyQty)

    const newBoard = boardArr.map(b => ({ ...b }))
    newBoard[index] = {
        ...bf,
        armyQty: bf.armyQty - fromArmyQty,
        movedInArmies: bf.movedInArmies - fromMovedIn,
        bases: [...basesOf(bf), { centerS, progress: 0, mature: false, hp: rules().fortStrength }],
        fortInConstruction: (bf.fortInConstruction || 0) + 1,
        pendingBaseBuilds: [...(bf.pendingBaseBuilds ?? []), { funded: consumed, centerS }],
        fortMovedInFunded: (bf.fortMovedInFunded || 0) + fromMovedIn,
        slots,
    }

    return { success: true, reason: null, boardArr: newBoard }
}

// Undoes one of this round's Base builds on corridor id (restores its 2
// veteran drones). `centerS` targets a specific pending build (tapping its
// ghost); omitted, the MOST RECENT build comes back first. The consumed
// tokens stored on the pending record return with their original identity;
// sorting the corridor's drones by uid restores the pre-build standing order
// exactly. Only this round's builds are undoable — pendingBaseBuilds is
// round-scoped (cleared at submit), so construction that has survived a
// battle keeps its investment.
// Blocked if restoring 2 drones would exceed corridor capacity (deploys may
// have taken the freed plots).
// Returns { success, reason, boardArr }
export function undoBuildFort(boardArr, id, centerS = null) {
    const index = boardArr.findIndex(bf => bf.id === id)
    if (index === -1) return { success: false, reason: `Corridor ${id} not found.`, boardArr }

    const bf = ensureSlots(boardArr[index])

    if ((bf.fortInConstruction || 0) === 0) {
        return { success: false, reason: 'No Base under construction to undo.', boardArr }
    }
    const pendings = bf.pendingBaseBuilds ?? []
    const pi = centerS != null
        ? pendings.findIndex(p => p.centerS === centerS)
        : pendings.length - 1
    const pending = pi >= 0 ? pendings[pi] : null
    if (!pending) {
        return { success: false, reason: 'Base construction that has survived a battle cannot be undone.', boardArr }
    }
    const cost = rules().broodCost
    if (totalUnits(bf) + cost > bf.maxArmyQty) {
        return { success: false, reason: 'Cannot undo Base construction. Corridor is full.', boardArr }
    }

    let nextUid = bf.nextUid || 1
    let restored = pending.funded
    if (!Array.isArray(restored) || restored.length !== cost) {
        const toMovedInLegacy = Math.min(bf.fortMovedInFunded || 0, cost)
        restored = []
        for (let i = 0; i < cost - toMovedInLegacy; i++) restored.push({ uid: nextUid++, kind: 'vet' })
        for (let i = 0; i < toMovedInLegacy; i++)     restored.push({ uid: nextUid++, kind: 'moved' })
    }
    const toMovedIn = restored.filter(t => t.kind === 'moved').length
    const toArmyQty = cost - toMovedIn

    const missiles = [...missilesOf(bf.slots), ...restored].sort((a, b) => a.uid - b.uid)
    const slots = packWithMissiles(bf.slots, bf.maxArmyQty, missiles)

    const bases = basesOf(bf).filter(b => b.centerS !== pending.centerS)
    const newBoard = boardArr.map(b => ({ ...b }))
    newBoard[index] = {
        ...bf,
        bases,
        fortInConstruction: bases.filter(b => !b.mature).length,
        pendingBaseBuilds: pendings.filter((_, i) => i !== pi),
        armyQty: bf.armyQty + toArmyQty,
        movedInArmies: bf.movedInArmies + toMovedIn,
        fortMovedInFunded: Math.max(0, (bf.fortMovedInFunded || 0) - toMovedIn),
        slots,
        nextUid,
    }

    return { success: true, reason: null, boardArr: newBoard }
}

// Refunds one FAILED Base build on a post-battle corridor: the build was
// declared (its 2 drones spent at submit) but the Grid stamp failed at
// resolution — addBaseStructure found the placement no longer legal — so the
// drones come back instead of vanishing into a Base that never existed.
// Deterministic (no uids carried across the wire survive the failure, so
// fresh vet tokens are minted from nextUid on both clients identically).
// Capacity-capped: refunded drones beyond maxArmyQty are lost — resupply
// deliveries may have taken the freed plots. Returns a new bf.
export function refundFailedBaseBuild(bf0) {
    const bf = ensureSlots(bf0)
    const space = Math.max(0, (bf.maxArmyQty || 0) - totalUnits(bf))
    const n = Math.min(rules().broodCost, space)
    if (n === 0) return bf
    let nextUid = bf.nextUid || 1
    const restored = []
    for (let i = 0; i < n; i++) restored.push({ uid: nextUid++, kind: 'vet' })
    const missiles = [...missilesOf(bf.slots), ...restored].sort((a, b) => a.uid - b.uid)
    const slots = packWithMissiles(bf.slots, bf.maxArmyQty, missiles)
    return { ...bf, armyQty: (bf.armyQty || 0) + n, slots, nextUid }
}

// ---------------------------------------------------------------------------
// Drop zones / resupply
// ---------------------------------------------------------------------------

// Drop zones the player must place before submitting: DROP_ZONES_PER_BASE per
// mature brood whose egg can fly. A brood whose egg died lays
// no more (ledger `egg`, mirrored from the Grid; an entry with no egg field
// is an intact brood). Bases under construction owe nothing, and neither do
// the broods of an ATTACKING corridor: they fire this round and their eggs
// die with them, so no airdrop is owed from (or paired with) them.
export const eggLays = (b) => !!b?.mature && (b.egg ?? 'egg') === 'egg'
export function requiredDropZones(boardArr) {
    const bases = (boardArr || []).reduce((sum, bf) => {
        if (bf?.isAttacking) return sum
        const ledger = basesOf(bf)
        return sum + (ledger.length ? ledger.filter(eggLays).length : (bf.fortCount || 0))
    }, 0)
    return bases * rules().dropZonesPerBase
}

// Validates placing a drop zone on the corridor at column `index`.
// Capacity governs placement: pending drop zones already aimed at this column
// count as occupants, so two markers can't claim the same last slot. Resolution
// only ever frees space (decisive battles clear the column; maturation keeps
// the unit count), so a placement that passes here is deliverable next round.
// Returns { valid, reason }
export function canPlaceDropZone(boardArr, index, pendingDropZones = []) {
    const bf = boardArr?.[index]
    if (!bf) return { valid: false, reason: `Corridor at column ${index} not found.` }
    const pendingHere = pendingDropZones.filter(col => col === index).length
    if (totalUnits(bf) + pendingHere >= bf.maxArmyQty) {
        return { valid: false, reason: 'Corridor is full. A drop zone needs an open slot.' }
    }
    return { valid: true, reason: null }
}

// The placed drop zones that still fit the board, in placement order: a
// zone whose slot a drone has since taken (moved or deployed into it) goes
// back to the player to place again. Returns the same array when every zone
// still fits.
export function fittingDropZones(boardArr, dropZoneCols = []) {
    const kept = []
    for (const col of dropZoneCols) if (canPlaceDropZone(boardArr, col, kept).valid) kept.push(col)
    return kept.length === dropZoneCols.length ? dropZoneCols : kept
}

// Which Base feeds each drop zone. Every mature Base earns one airdrop a
// round (DROP_ZONES_PER_BASE), and the airdrop LAUNCHES FROM THAT BASE: the
// board draws a tether from the Base to the zone while it is placed and
// flies the crate along that arc at delivery. The pairing is a pure rule
// of the board and the zone list, so the placing client and both reveals
// agree without syncing anything extra: each zone, in placement order,
// takes the nearest still-unassigned mature Base whose egg can fly (not one
// in an attacking corridor) — by corridor distance,
// ties to the lower corridor, then ledger order. Returns one entry per
// zone: { col, centerS } of its Base, or null when no Base is left for it
// (a zone owed by a Base that has since died stays a zone; it just has no
// launch point).
export function dropZoneSources(boardArr, dropZoneCols = []) {
    const pool = []
    ;(boardArr || []).forEach((bf, col) => {
        if (bf?.isAttacking) return
        for (const b of basesOf(bf)) {
            if (!eggLays(b)) continue
            for (let k = 0; k < rules().dropZonesPerBase; k++) pool.push({ col, centerS: b.centerS })
        }
    })
    return dropZoneCols.map(zoneCol => {
        let best = -1
        pool.forEach((b, i) => {
            if (!b) return
            if (best === -1 || Math.abs(b.col - zoneCol) < Math.abs(pool[best].col - zoneCol)) best = i
        })
        if (best === -1) return null
        const src = pool[best]
        pool[best] = null
        return { col: src.col, centerS: src.centerS }
    })
}

// Delivers resupply missiles onto a post-battle board: +1 NEW missile
// (newArmies, same as a fresh deploy) per declared drop-zone column — green,
// on the ground, unable to move or attack until next round's submit lifts it
// off as a veteran (matureCorridor). Delivery happens after battle clearing
// and base maturation, so a missile arrives even when its corridor was wiped
// — placement-time capacity checks guarantee room, and the skip-if-full
// guard is only a safety net.
// `sources` (optional) is dropZoneSources' answer for the same columns,
// computed from the board the zones were PLACED on (the pre-battle board):
// the delivered crates' launch points come back aligned with `delivered`.
// `blocked` (optional) is the set of source centres whose egg can no longer
// fly — the brood FIRED this round (the egg dies with it), or one of its
// drones or its nest was cratered (the egg dies with its parent) — so that
// zone's delivery is skipped.
// Returns { boardArr, delivered, skipped, sources } (delivered/skipped are
// column lists; sources one { col, centerS } | null per delivered column)
export function applyResupplyDeliveries(boardArr, dropZoneCols = [], sources = null, blocked = null) {
    const newBoard = boardArr.map(b => ({ ...b }))
    const delivered = []
    const skipped = []
    const deliveredFrom = []
    dropZoneCols.forEach((col, i) => {
        const bf0 = newBoard[col]
        const src = sources?.[i] ?? null
        if (!bf0 || totalUnits(bf0) >= bf0.maxArmyQty || (src && blocked?.has(src.centerS))) {
            skipped.push(col)
            return
        }
        const bf = ensureSlots(bf0)
        newBoard[col] = {
            ...bf,
            newArmies: (bf.newArmies || 0) + 1,
            // Delivered missiles are bound to their landing column: unlike a
            // user deploy they cannot be removed back into armiesToDeploy
            // (undoDeploy guards on this count; submit merge resets it).
            deliveredNewArmies: (bf.deliveredNewArmies || 0) + 1,
            slots: appendToken(bf, { uid: bf.nextUid || 1, kind: 'green', delivered: true }),
            nextUid: (bf.nextUid || 1) + 1,
        }
        delivered.push(col)
        deliveredFrom.push(sources?.[i] ?? null)
    })
    return { boardArr: newBoard, delivered, skipped, sources: deliveredFrom }
}

// ---------------------------------------------------------------------------
// Round-end merge
// ---------------------------------------------------------------------------

// Submit-time merge for one corridor: greens and moved-in missiles mature into
// veterans IN PLACE — their plots never change, only the tile's look (a green
// keeps its uid, so the board sees the same tile lift off the ground into a
// flying veteran) — and the round-scoped flags reset.
export function matureCorridor(bf0) {
    const bf = ensureSlots(bf0)
    return {
        ...bf,
        armyQty: (bf.armyQty || 0) + (bf.newArmies || 0) + (bf.movedInArmies || 0),
        newArmies: 0,
        deliveredNewArmies: 0,
        movedInArmies: 0,
        movedFrom: null,
        movedOut: false,
        // The moved-in-funded ledger and the pending build records are
        // round-scoped (they only inform undoBuildFort); stale ones would
        // misroute next round's undos.
        fortMovedInFunded: 0,
        pendingBaseBuilds: null,
        ...(bf.attackHold > 0 ? { attackHold: bf.attackHold - 1 } : {}),
        slots: bf.slots.map(t => t && (t.kind === 'green' || t.kind === 'moved')
            ? { uid: t.uid, kind: 'vet' } : t),
    }
}

// ---------------------------------------------------------------------------
// Opponent round replay
// ---------------------------------------------------------------------------
//
// Players kept missing what their opponent did each round — the new defense
// just sat there under the frost, and the clash outcome came as a surprise.
// So before the first clash spotlights, the opponent's half briefly replays
// their round: movements first, then fresh deploys.

// buildRoundActionsLedger — captured at submit time from the PRE-matured board
// (matureCorridor erases the moved/green kinds and the movedFrom/movedOut
// flags this needs). Synced as p{n}RoundActions so the opponent's client can
// replay what this player did. Per corridor:
//   mi   uids of drones that moved in from a neighbour this round
//   mif  uids of drones that moved in and then FUNDED a Base built this
//        round — consumed into a pending build's funded record, so they're
//        absent from the submitted slots but their hop must still replay
//   g    uids of drones deployed fresh this round (resupply deliveries
//        excluded — their parachute drop already played during last reveal)
//   bu   one uid-PAIR per Base built this round, in build order (several
//        builds per corridor per round are legal) — each pair's descent
//        motes fly to its own footprint
//   from side the (last) moved-in drone arrived from
//   out  whether this corridor's move was spent (its front veteran left)
export function buildRoundActionsLedger(boardArr) {
    return boardArr.map(bf0 => {
        const bf = ensureSlots(bf0)
        const mi = []
        const mif = []
        const g = []
        const bu = []
        for (const t of bf.slots) {
            if (!t) continue
            if (t.kind === 'moved') mi.push(t.uid)
            else if (t.kind === 'green' && !t.delivered) g.push(t.uid)
        }
        for (const p of (bf.pendingBaseBuilds ?? [])) {
            bu.push((p.funded || []).map(f => f.uid))
            for (const f of (p.funded || [])) if (f.kind === 'moved') mif.push(f.uid)
        }
        return { mi, mif, g, bu, from: bf.movedFrom || null, out: !!bf.movedOut }
    })
}

// buildOppReplaySteps — turns (previous board, submitted board, actions
// ledger) into a list of display boards, each held for holdMs:
//   • one board per cross-corridor move — the lone appear/disappear pair lets
//     HexBoard's FLIP effect play the same sideways hop a live move gets
//   • one board per corridor that built a Base — the two funding drones
//     vanish (the Base itself rises on the Grid at the frost-lift, not in
//     the corridor)
//   • one board landing every fresh deploy at once — HexBoard glides them in
//     from the rear edge with a stagger (entrance:'deploy')
//   • the submitted board itself, so the hand-off to the clash is seamless
//
// Every step keeps each corridor's counts in lockstep with its slots —
// buildArmyMap runs ensureSlots, which would re-uid a drifted corridor and
// break the animation keys. Anything the ledger can't explain (uid rebuilds,
// an old client that never wrote a ledger) returns null: no replay, current
// behavior.
export function buildOppReplaySteps(fromBoard, toBoard, ledger) {
    if (!Array.isArray(fromBoard) || !Array.isArray(toBoard) || !Array.isArray(ledger)) return null
    if (fromBoard.length !== toBoard.length || ledger.length !== toBoard.length) return null
    const from = fromBoard.map(bf => ensureSlots(bf))
    const to   = toBoard.map(bf => ensureSlots(bf))
    const fromUids = from.map(bf => new Set(bf.slots.filter(Boolean).map(t => t.uid)))
    const toSlotOf = to.map(bf => {
        const m = new Map()
        bf.slots.forEach((t, i) => { if (t) m.set(t.uid, i) })
        return m
    })
    const acts = ledger.map(l => ({
        mi: l?.mi || [], mif: l?.mif || [], g: l?.g || [],
        // bu is one uid-pair per build; older ledgers carried a flat list —
        // normalize to pairs-of-arrays so both shapes replay.
        bu: (l?.bu || []).length && !Array.isArray((l.bu)[0]) ? [l.bu] : (l?.bu || []),
        from: l?.from || null, out: !!l?.out,
    }))
    const flatBu = acts.map(a => a.bu.flat())

    // Uid continuity both ways. Every ledger uid must exist in the submitted
    // board (except mif arrivals — consumed into a fresh Base's funding, they
    // must NOT, and need that corridor's build beat to carry them off), every
    // gained token must be a ledger arrival, and every lost token must be the
    // spent move or a build's funded pair.
    for (let c = 0; c < to.length; c++) {
        const a = acts[c]
        for (const uid of [...a.mi, ...a.g]) if (!toSlotOf[c].has(uid)) return null
        for (const uid of a.mif) if (toSlotOf[c].has(uid)) return null
        if (a.mif.length && !flatBu[c].length) return null
        const explained = new Set([...a.mi, ...a.g])
        for (const uid of toSlotOf[c].keys()) {
            if (!fromUids[c].has(uid) && !explained.has(uid)) return null
        }
        const gone = [...fromUids[c]].filter(uid => !toSlotOf[c].has(uid))
        if (!flatBu[c].length && gone.length > (a.out ? 1 : 0)) return null
    }

    // Attribute each moved-in missile to its source corridor. A corridor moves
    // out at most one missile per round, so sources are just the out-flagged
    // neighbours; prefer the recorded arrival side, falling back to the other
    // neighbour (a corridor fed from both sides records only the last side).
    const sourceFree = acts.map(a => a.out)
    const moves = []
    for (let c = 0; c < to.length; c++) {
        // mif arrivals hop like any move — they just get consumed by the
        // corridor's Base build beat afterwards instead of surviving.
        for (const uid of [...acts[c].mi, ...acts[c].mif]) {
            const preferred = acts[c].from === 'right' ? c + 1 : c - 1
            const other     = acts[c].from === 'right' ? c - 1 : c + 1
            const src = sourceFree[preferred] ? preferred : (sourceFree[other] ? other : null)
            if (src == null) return null
            sourceFree[src] = false
            moves.push({ src, dest: c, uid })
        }
    }

    const working = from.map(bf => ({ ...bf, slots: [...bf.slots] }))
    const snap = () => working.map(bf => ({ ...bf, slots: [...bf.slots] }))
    const removeUid = (bf, uid, pack = true) => {
        const i = bf.slots.findIndex(t => t && t.uid === uid)
        if (i === -1) return false
        const t = bf.slots[i]
        if (t.kind === 'vet')          bf.armyQty = (bf.armyQty || 0) - 1
        else if (t.kind === 'moved')   bf.movedInArmies = (bf.movedInArmies || 0) - 1
        else if (t.kind === 'green') {
            bf.newArmies = (bf.newArmies || 0) - 1
            if (t.delivered) bf.deliveredNewArmies = Math.max(0, (bf.deliveredNewArmies || 0) - 1)
        }
        bf.slots[i] = null
        if (pack) bf.slots = packSlots(bf.slots, bf.maxArmyQty)
        return true
    }
    // A moved-in drone packs in ahead of the greens (insertMovedToken — the
    // live move's rule, so the hop lands where the submitted board holds it
    // even though the deploys haven't been shown yet). A fresh deploy lands
    // on its submitted plot when it's open, else the first open one — the
    // final step slides any drift into place.
    const placeToken = (bf, c, token) => {
        if (bf.slots.indexOf(null) === -1) return false
        if (token.kind === 'moved') {
            bf.slots = insertMovedToken(bf.slots, bf.maxArmyQty, token)
            bf.movedInArmies = (bf.movedInArmies || 0) + 1
            return true
        }
        const want = toSlotOf[c].get(token.uid)
        const slot = (want != null && bf.slots[want] == null) ? want : bf.slots.indexOf(null)
        bf.slots[slot] = token
        bf.newArmies = (bf.newArmies || 0) + 1
        return true
    }

    const steps = []

    // ── Movement beats ── one per move, so each hop reads on its own.
    for (const mv of moves) {
        const src = working[mv.src]
        const leaver = src.slots.find(t => t && t.kind === 'vet' && !toSlotOf[mv.src].has(t.uid))
        if (!leaver || !removeUid(src, leaver.uid)) return null
        if (!placeToken(working[mv.dest], mv.dest, { uid: mv.uid, kind: 'moved' })) return null
        steps.push({ board: snap(), holdMs: REPLAY_MOVE_MS })
    }

    // ── Base-build beats ── the corridor's funding drones lift off and fly
    // down to the Grid (the caller launches the descent motes off buildCol/
    // buildPairs just before applying this step's board — one pair per fresh
    // Base), and the constructions appear on their footprints as they land.
    // Several builds in one corridor share ONE beat.
    for (let c = 0; c < to.length; c++) {
        if (!flatBu[c].length) continue
        const bf = working[c]
        // NO re-pack on this beat: the descent motes are the only motion —
        // neighbours sliding into the vacated plots at the same moment reads
        // as chaos. The settle step (the packed submitted board) tidies up.
        for (const t of bf.slots.filter(t => t && !toSlotOf[c].has(t.uid))) removeUid(bf, t.uid, false)
        // The submitted board already carries the corridor's fort fields —
        // mirror them so the hand-off step doesn't jump.
        bf.fortInConstruction = to[c].fortInConstruction
        bf.bases = to[c].bases
        steps.push({ board: snap(), holdMs: REPLAY_BUILD_MS, buildCol: c, buildPairs: acts[c].bu.map(p => [...p]) })
    }

    // ── Deploy beat ── all fresh deploys land in one staggered step.
    const deployCount = acts.reduce((n, a) => n + a.g.length, 0)
    if (deployCount) {
        for (let c = 0; c < to.length; c++) {
            for (const uid of acts[c].g) {
                if (!placeToken(working[c], c, { uid, kind: 'green', entrance: 'deploy' })) return null
            }
        }
        steps.push({
            board: snap(),
            holdMs: REPLAY_DEPLOY_GLIDE_MS + REPLAY_DEPLOY_STAGGER_MS * (deployCount - 1) + 260,
        })
    }

    if (!steps.length) return null
    steps.push({ board: to, holdMs: REPLAY_SETTLE_MS })
    return steps
}
